import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import ActivityFeedView from "components/lib/ActivityFeed";

const ActivityFeed = () => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const [feedState, setFeedState] = useState<
    OpenAPI.GET<"/users/feed">["response"] & {
      loading: boolean;
      extending: boolean;
      isFeed: boolean;
    }
  >({
    feed: [],
    loading: false,
    canExtend: false,
    extending: false,
    isFeed: false,
  });
  const [parameterState, setParameterState] = useState({
    show: [
      "idea",
      "challenge",
      "achievement",
      "organisationActivityFeedPosts",
      "group",
    ],
    feedType: "all",
    limit: 25,
    page: 1,
  });
  const { loading, feed, canExtend, extending, isFeed } = feedState;
  const { limit, page, show, feedType } = parameterState;
  const orgId = user?.ownerOrganisation?._id;

  const feedOptions = [
    {
      key: "idea",
      text: t("common:capitalise", { key: "generic.ideas" }),
      value: "idea",
    },
    {
      key: "challenge",
      text: t("common:capitalise", { key: "generic.challenges" }),
      value: "challenge",
    },
    {
      key: "achievement",
      text: t("generic.achievements"),
      value: "achievement",
    },
    {
      key: "organisationActivityFeedPosts",
      text: t("generic.organisations"),
      value: "organisationActivityFeedPosts",
    },
    {
      key: "group",
      text: t("common:capitalise", { key: "generic.groups" }),
      value: "group",
    },
  ];

  const getFeed = useCallback(() => {
    setFeedState((prevState) => ({ ...prevState, loading: true }));
    api.users.feed(
      { limit: 25, page: 1, include: show },
      (data) => {
        setFeedState((prevState) => ({
          ...prevState,
          ...data,
          loading: false,
          isFeed: true,
        }));
      },
      (err) => {
        toast.error(err.message);
        setFeedState((prevState) => ({ ...prevState, loading: false }));
      },
    );
  }, [show]);

  useEffect(() => {
    getFeed();
  }, [getFeed, user?.ownerOrganisation?._id]);

  const extendFeed = useCallback(() => {
    setFeedState((prevState) => ({ ...prevState, extending: true }));
    api.users.feed(
      { limit, page: page + 1, include: show },
      (data) => {
        setFeedState((prevState) => ({
          ...prevState,
          feed: [...prevState.feed, ...data.feed],
          extending: false,
        }));
        setParameterState((prevState) => ({ ...prevState, page: page + 1 }));
      },
      (err) => {
        toast.error(err.message);
        setFeedState((prevState) => ({ ...prevState, extending: false }));
      },
    );
  }, [limit, page, show]);

  const handleFeedType = useCallback(
    ({ value }) => {
      const isShown = show.indexOf(value) > -1;

      if (!isShown) {
        setParameterState((prevState) => ({
          ...prevState,
          show: [value, ...prevState.show],
        }));
      } else {
        setParameterState((prevState) => ({
          ...prevState,
          show: prevState.show.filter((f) => f !== value),
        }));
      }
      setFeedState((prevState) => ({
        ...prevState,
        loading: true,
        isFeed: false,
      }));
    },
    [show],
  );

  const onPost = useCallback(
    (post) => {
      api.organisations.postToActivityFeed(
        orgId,
        post,
        (data) => {
          setFeedState((prevState) => ({
            ...prevState,
            feed: [
              {
                ...data.post,
                owner: post.isAnonymous ? null : user,
                type: "organisationActivityFeedPost",
              },
              ...prevState.feed,
            ],
          }));
        },
        (err) => toast.error(err.message),
      );
    },
    [orgId, user],
  );

  const onUpdatePost = useCallback(
    (postId, updateData) => {
      api.organisations.updateActivityFeedPost(
        orgId,
        postId,
        updateData,
        ({ post: updatedPost }) => {
          setFeedState((prevState) => {
            const postIndex = prevState.feed.findIndex((p) => p._id === postId);
            const updatedFeed = [...prevState.feed];
            updatedFeed.splice(postIndex, 1, {
              ...prevState.feed[postIndex],
              ...updatedPost,
              isPinned: !!updatedPost.isPinned,
            });
            return { ...prevState, feed: updatedFeed };
          });
        },
        (err) => toast.error(err.message),
      );
    },
    [orgId],
  );

  const onDeletePost = useCallback(
    (postId) => {
      api.organisations.deletePostFromActivityFeed(
        orgId,
        postId,
        () => {
          setFeedState((prevState) => ({
            ...prevState,
            feed: prevState.feed.filter((p) => p._id !== postId),
          }));
        },
        (err) => toast.error(err.message),
      );
    },
    [orgId],
  );

  return (
    <ActivityFeedView
      loading={loading}
      feed={feed}
      feedType={feedType}
      isFeed={isFeed}
      feedOptions={feedOptions}
      handleFeedType={handleFeedType}
      extendFeed={extendFeed}
      canExtend={canExtend}
      show={show}
      extending={extending}
      canPost={util.hasPermission(
        user,
        "org.viewDashboard",
        user?.ownerOrganisation._id,
      )}
      onPost={onPost}
      onUpdatePost={onUpdatePost}
      onDeletePost={onDeletePost}
    />
  );
};

export default ActivityFeed;
